import { Link } from "react-router-dom";

const HERO = "/brand/britannia_studio.jpg";
const BRITANNIA_LOGO = "/brand/britannia_white.png";
const IMG_2 = "/brand/britannia_meanwhile_gallery.jpg";
const IMG_3 = "/brand/britannia_meanwhile_studio.jpg";
const IMG_4 = "/brand/britannia_heritage.webp";

const facts = [
  { l: "Location", v: "Islington, London N1" },
  { l: "Status", v: "Planning in progress" },
  { l: "Meanwhile", v: "Florence Trust and Sentient Projects" },
];

export default function BritanniaRow() {
  return (
    <div data-testid="britannia-page">
      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0">
          <img src={HERO} alt="35 Britannia Row — former Pink Floyd studios" className="w-full h-full object-cover object-center opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-b from-[rgba(35,27,25,0.35)] via-[rgba(35,27,25,0.55)] to-[color:var(--hm-bg)]" />
        </div>
        <div className="relative hm-container pt-40 pb-40 md:pt-56 md:pb-64">
          <h1 className="font-display leading-[0.94] tracking-[-0.02em] max-w-5xl" style={{ fontSize: "clamp(52px, 8vw, 120px)", fontWeight: 400 }}>
            Britannia Row
          </h1>
          <p className="mt-10 max-w-2xl text-[19px] text-[color:var(--hm-ink)] leading-relaxed">
            The former Pink Floyd recording studios — a heritage restoration in active partnership with cultural use.
          </p>
          <img
            src={BRITANNIA_LOGO}
            alt="Britannia"
            className="mt-14 max-w-[540px] w-full h-auto opacity-95"
            data-testid="britannia-logo-hero"
          />
        </div>
      </section>

      {/* Facts */}
      <section className="hm-container -mt-20 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[color:var(--hm-line)] border border-[color:var(--hm-line)]">
          {facts.map((f) => (
            <div key={f.l} className="bg-[color:var(--hm-bg-2)] p-7">
              <p className="hm-eyebrow mb-3">{f.l}</p>
              <p className="font-display text-[20px] leading-tight" style={{ fontWeight: 400 }}>{f.v}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Current use */}
      <section className="hm-container py-28 md:py-36">
        <div className="grid md:grid-cols-12 gap-14">
          <div className="md:col-span-5">
            <p className="hm-eyebrow mb-6">Now — meanwhile use</p>
            <h2 className="font-display leading-[1.05] tracking-[-0.01em]" style={{ fontSize: "clamp(32px, 4vw, 54px)", fontWeight: 400 }}>
              The building is alive while planning moves forward.
            </h2>
          </div>
          <div className="md:col-span-7 space-y-5 text-[17px] text-[color:var(--hm-ink-dim)] leading-relaxed">
            <p>
              Britannia Row is currently running as a meanwhile-use cultural space. In partnership with the <a href="https://www.theflorencetrust.com" target="_blank" rel="noopener noreferrer" className="hm-link text-[color:var(--hm-ink)]" data-testid="britannia-florence-trust-link">Florence Trust</a>, a highly-regarded artist residency programme, 18 artists work in newly created studios, while three gallery spaces offer a rotating schedule of exhibitions and performances.
            </p>
            <p>
              Har-Monic co-manages a separate programme inside the building called <span className="text-[color:var(--hm-pink)]">Sentient Projects</span> — dedicated to immersive experiences centred around art, music and ritual.
            </p>
            <p>
              This continues while redevelopment plans move through planning. It is a deliberate continuation of what the building has always been: a place where people make things.
            </p>
            <div className="pt-4">
              <Link
                to="/projects/35-britannia-row/events"
                className="hm-btn"
                data-testid="britannia-events-link"
              >
                See what&rsquo;s on →
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section>
        <div className="hm-container">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="aspect-[4/3] overflow-hidden">
              <img src={IMG_2} alt="Artist studio at Britannia Row — Florence Trust residency" className="w-full h-full object-cover" />
            </div>
            <div className="aspect-[4/3] overflow-hidden">
              <img src={IMG_3} alt="Exhibition inside Britannia Row" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Next */}
      <section className="hm-container py-28 md:py-36">
        <div className="grid md:grid-cols-12 gap-14">
          <div className="md:col-span-5">
            <p className="hm-eyebrow mb-6">Next — restoration & redevelopment</p>
            <h2 className="font-display leading-[1.05] tracking-[-0.01em]" style={{ fontSize: "clamp(32px, 4vw, 54px)", fontWeight: 400 }}>
              Heritage restoration.
            </h2>
          </div>
          <div className="md:col-span-7 space-y-5 text-[17px] text-[color:var(--hm-ink-dim)] leading-relaxed">
            <p>
              A full design and planning process is underway to unlock additional capacity and create a mixed-use scheme that serves as both residential and community use.
            </p>
            <p>
              We are bringing a culturally significant building that was vacant for over a decade back to life — for residents, for the neighbourhood, and for the cultural lineage already running through it.
            </p>
          </div>
        </div>
      </section>

      <section className="relative">
        <div className="aspect-[21/9] overflow-hidden">
          <img src={IMG_4} alt="Interior wall at 35 Britannia Row — 'Art tells gorgeous lies that come true'" className="w-full h-full object-cover" />
        </div>
      </section>

      {/* CTA */}
      <section className="hm-container py-28">
        <div className="border border-[color:var(--hm-line)] p-10 md:p-16 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <div>
            <p className="hm-eyebrow mb-4">Investor & partnership enquiries</p>
            <h3 className="font-display max-w-2xl leading-[1.05]" style={{ fontSize: "clamp(26px, 3.2vw, 42px)", fontWeight: 400 }}>
              Interested in Britannia Row, the meanwhile programme, or the broader pipeline?
            </h3>
          </div>
          <Link to="/contact" className="hm-btn shrink-0" data-testid="britannia-cta-contact">Get in touch →</Link>
        </div>
      </section>
    </div>
  );
}
